// src/utils/generarContratoPDF.js
// Genera el PDF del Contrato de Afiliación y Aportes con los datos del formulario

import { jsPDF } from 'jspdf';

// Paleta corporativa
const VERDE  = [26, 77, 46];   // #1a4d2e
const VERDE2 = [46, 125, 50];  // #2e7d32
const DORADO = [200, 168, 75]; // #c8a84b
const GRIS   = [100, 100, 100];
const NEGRO  = [30, 30, 30];
const GRIS_CLARO = [240, 240, 240];

function addPageHeader(doc, pageNum) {
  // Barra verde superior
  doc.setFillColor(...VERDE);
  doc.rect(0, 0, 210, 18, 'F');

  // Barra dorada
  doc.setFillColor(...DORADO);
  doc.rect(0, 18, 210, 2, 'F');

  // Texto del encabezado
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('FEDERACIÓN NACIONAL AGROINDUSTRIA AMBIENTAL Y PRODUCCIÓN LIMPIA "FENAGRO APL"', 14, 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('NIT: 901026305-2  ·  Carrera 96 G No 22 G 21, Bogotá D.C.  ·  300 776 54 00  ·  fondoemprendimientofenagro@gmail.com', 14, 14);

  // Número de página
  doc.setFontSize(8);
  doc.setTextColor(200, 200, 200);
  doc.text(`Página ${pageNum}`, 196, 14, { align: 'right' });

  return 26; // y inicial después del header
}

function addPageFooter(doc) {
  const py = 287;
  doc.setDrawColor(...VERDE);
  doc.setLineWidth(0.3);
  doc.line(14, py, 196, py);
  doc.setFontSize(7);
  doc.setTextColor(...GRIS);
  doc.text('Carrera 96 G No 22 G 21 Bogotá D.C – 300 776 54 00  fondoemprendimientofenagro@gmail.com – web. https://www.fenagroapl.com.co', 14, py + 4);
  doc.text('República Popular China sede BEIJING No calle de Haihe, Mansion de Wanruida No150, edf, 1, No 1107-60, Área Libre de Aranceles de Dalian, Provincia de Liaoning.', 14, py + 8);
}

function sectionTitle(doc, y, text) {
  doc.setFillColor(...VERDE);
  doc.rect(14, y, 182, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(255, 255, 255);
  doc.text(text, 16, y + 5);
  return y + 10;
}

function clausulaTitle(doc, y, number, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...VERDE2);
  doc.text(`${number}: ${title.toUpperCase()}`, 14, y);
  return y + 5;
}

function bodyText(doc, y, text, maxWidth = 182, indent = 14) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...NEGRO);
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, indent, y);
  return y + lines.length * 4;
}

function campo(doc, y, label, value, x = 14, w = 86) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...GRIS);
  doc.text(label + ':', x, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...NEGRO);
  doc.text(String(value || '—'), x + 30, y);
  // Línea bajo el campo
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.2);
  doc.line(x, y + 1, x + w, y + 1);
  return y + 5.5;
}

function fmtFecha(val) {
  if (!val) return '—';
  try {
    return new Date(val + 'T00:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return val; }
}

function fmtMoneda(val) {
  return '$' + Number(val || 0).toLocaleString('es-CO') + ' COP';
}

const ESTADO_CIVIL_MAP = {
  SOLTERO: 'Soltero(a)', CASADO: 'Casado(a)', DIVORCIADO: 'Divorciado(a)',
  VIUDO: 'Viudo(a)', UNION_LIBRE: 'Unión libre', OTRO: 'Otro',
};

const GENERO_MAP = { M: 'Masculino', F: 'Femenino', NB: 'No binario', O: 'Otro' };

const MODALIDAD_MAP = {
  DINERO: 'Dinero en efectivo / transferencia',
  TRABAJO: 'Trabajo y Servicios (horas colaborativas)',
  BIENES: 'Bienes en especie',
};

// ── FUNCIÓN PRINCIPAL ──────────────────────────────────────────────────────
export function generarContratoPDF(form, afiliadoId, opcionesGeo = {}, hashContrato = null) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y;
  let pagina = 1;

  function checkPage(needed = 20) {
    if (y + needed > 278) {
      addPageFooter(doc);
      doc.addPage();
      pagina++;
      y = addPageHeader(doc, pagina);
    }
  }

  // ── PÁGINA 1 ──────────────────────────────────────────────────────────
  y = addPageHeader(doc, pagina);

  // Título del contrato
  y += 4;
  doc.setFillColor(...GRIS_CLARO);
  doc.rect(14, y, 182, 10, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...VERDE);
  doc.text('CONTRATO O CONVENIO ASOCIATIVO DE AFILIADO Y APORTES EN ECONOMÍAS COLABORATIVAS', 105, y + 7, { align: 'center' });
  y += 14;

  // Número de contrato y fecha
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRIS);
  const fechaHoy = new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.text(`No. ${afiliadoId ? String(afiliadoId).padStart(6,'0') : '______'}-2025  ·  Fecha: ${fechaHoy}`, 105, y, { align: 'center' });
  y += 8;

  // ── I. CONTRATO DE AFILIACIÓN ──
  y = sectionTitle(doc, y, 'I. CONTRATO DE AFILIACIÓN Y APORTES');
  y += 2;

  y = bodyText(doc, y, `Entre los suscritos: JAVIER ARMANDO GIL CARMONA, identificado con C.C. 71.392.618, representante legal de la FEDERACIÓN NACIONAL AGROINDUSTRIA AMBIENTAL Y PRODUCCIÓN LIMPIA "FENAGRO APL", NIT: 901026305-2, con domicilio en Carrera 96 G N 22 G 21, Bogotá D.C., en adelante denominada la Federación "FENAGRO APL"; y de otra parte:`);
  y += 2;

  y = clausulaTitle(doc, y, 'CLÁUSULA SEGUNDA', 'Formato de Afiliación');

  // Datos personales en dos columnas
  const col1x = 14, col2x = 105;

  doc.setFillColor(...GRIS_CLARO);
  doc.rect(14, y, 182, 6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...VERDE);
  doc.text('DECLARACIÓN DE BENEFICIARIOS DEL ASOCIADO', 105, y + 4, { align: 'center' });
  y += 9;

  // Fila 1
  campo(doc, y, 'Nombres', form.nombre, col1x, 85);
  campo(doc, y, 'Apellidos', form.apellidos, col2x, 85);
  y += 6;

  campo(doc, y, 'Cédula', form.cedula, col1x, 85);
  campo(doc, y, 'Expedida en', form.cedulaExpedida || '—', col2x, 85);
  y += 6;

  campo(doc, y, 'F. Nacimiento', fmtFecha(form.fechaNacimiento), col1x, 85);
  campo(doc, y, 'Género', GENERO_MAP[form.genero] || form.genero, col2x, 85);
  y += 6;

  campo(doc, y, 'Estado Civil', ESTADO_CIVIL_MAP[form.estadoCivil] || form.estadoCivil || '—', col1x, 85);
  campo(doc, y, 'Código Alfa Num.', form.codigoAlfaNum || '—', col2x, 85);
  y += 6;

  // Ubicación
  const deptNombre = opcionesGeo.departamento || `ID: ${form.departamentoId || '—'}`;
  const muniNombre = opcionesGeo.municipio    || `ID: ${form.municipioId    || '—'}`;

  campo(doc, y, 'País', opcionesGeo.pais || 'Colombia', col1x, 85);
  campo(doc, y, 'Departamento', deptNombre, col2x, 85);
  y += 6;

  campo(doc, y, 'Ciudad / Mun.', muniNombre, col1x, 85);
  campo(doc, y, 'Cód. Postal', form.codigoPostal || '—', col2x, 85);
  y += 6;

  campo(doc, y, 'Dirección', form.direccion || '—', col1x, 182);
  y += 6;

  campo(doc, y, 'Celular', `+57 ${form.celular}`, col1x, 85);
  campo(doc, y, 'Celular 2', form.celular2 ? `+57 ${form.celular2}` : '—', col2x, 85);
  y += 6;

  campo(doc, y, 'Email', form.email, col1x, 182);
  y += 8;

  // ── CLAUSULA TERCERA — Beneficiarios ──
  checkPage(30);
  y = clausulaTitle(doc, y, 'CLÁUSULA CUARTA', 'Declaración de Beneficiarios de la Cuenta de Emprendimiento');
  y = bodyText(doc, y, 'El asociado tomador de los programas de la Confederación "ALIANZA SOLIDARIA" nombra como beneficiarios a las siguientes personas, quienes en caso de fallecimiento del titular podrán disponer del saldo del contrato en partes iguales:');
  y += 3;

  // Tabla de beneficiarios
  const bens = (form.beneficiarios || []).filter(b => b.nombres?.trim() && b.apellidos?.trim());
  if (bens.length > 0) {
    // Encabezado tabla
    doc.setFillColor(...VERDE2);
    doc.rect(14, y, 182, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text('Nombres', 16, y + 4);
    doc.text('Apellidos', 68, y + 4);
    doc.text('Cédula', 120, y + 4);
    doc.text('Celular / Email', 155, y + 4);
    y += 7;

    bens.forEach((b, i) => {
      doc.setFillColor(i % 2 === 0 ? 248 : 255, i % 2 === 0 ? 253 : 255, i % 2 === 0 ? 248 : 255);
      doc.rect(14, y - 1, 182, 6, 'F');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...NEGRO);
      doc.text(b.nombres || '', 16, y + 3);
      doc.text(b.apellidos || '', 68, y + 3);
      doc.text(b.cedula || '—', 120, y + 3);
      doc.text(b.contacto || '—', 155, y + 3);
      y += 6;
    });
    y += 3;
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor(...GRIS);
    doc.text('No se designaron beneficiarios.', 14, y);
    y += 7;
  }

  // ── PÁGINA 2 — APORTES ──
  checkPage(50);
  y = sectionTitle(doc, y, 'III. FORMATO DE APORTE');
  y += 2;

  y = clausulaTitle(doc, y, 'CLÁUSULA SEXTA', 'Formato de Aporte');
  y += 2;

  // Tabla de aportes
  const aportes = [
    ['Afiliación a la cooperativa',           fmtMoneda(60000),  'Pago único — cuota de inscripción'],
    ['Afiliación red Consumo Colaborativo',    fmtMoneda(60000),  'Plataforma digital de pro$umo colaborativo'],
    ['Aporte mínimo mensual',                 fmtMoneda(400000), 'Consumo colaborativo en establecimientos autorizados'],
  ];

  doc.setFillColor(...VERDE2);
  doc.rect(14, y, 182, 6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  doc.text('Concepto', 16, y + 4);
  doc.text('Valor', 130, y + 4);
  doc.text('Descripción', 155, y + 4);
  y += 7;

  aportes.forEach(([concepto, valor, desc], i) => {
    doc.setFillColor(i % 2 === 0 ? 248 : 255, i % 2 === 0 ? 253 : 255, i % 2 === 0 ? 248 : 255);
    doc.rect(14, y - 1, 182, 6, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...NEGRO);
    doc.text(concepto, 16, y + 3);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...VERDE2);
    doc.text(valor, 130, y + 3);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRIS);
    doc.text(desc, 155, y + 3);
    y += 6;
  });
  y += 4;

  // Modalidad de aporte
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...NEGRO);
  doc.text('Modalidad de pago seleccionada:', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...VERDE2);
  doc.text(MODALIDAD_MAP[form.modalidadAporte] || form.modalidadAporte || '—', 80, y);
  y += 5;

  if (form.fechaPrimeraCuota) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NEGRO);
    doc.text('Fecha estimada primera cuota:', 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(fmtFecha(form.fechaPrimeraCuota), 80, y);
    y += 5;
  }

  // Datos bancarios
  y += 2;
  doc.setFillColor(255, 248, 225);
  doc.rect(14, y, 182, 14, 'F');
  doc.setDrawColor(...DORADO);
  doc.setLineWidth(0.5);
  doc.rect(14, y, 182, 14);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...NEGRO);
  doc.text('Cuenta para consignación de aportes en dinero:', 16, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('FENAGRO APL  ·  Cuenta de Ahorros No. 58900042-27  ·  NIT: 901026305-2', 16, y + 10);
  y += 18;

  // ── CLÁUSULAS RESUMIDAS ──
  checkPage(40);
  y = sectionTitle(doc, y, 'IV. OBLIGACIONES Y TÉRMINOS');
  y += 2;

  const clausulas = [
    ['CL. 7ª — Duración', 'El presente contrato tendrá vigencia mientras duren los programas de Emprendimiento de la confederación "ALIANZA SOLIDARIA". Podrá revocarse por mutuo acuerdo o prorrogarse mediante contrato adicional escrito.'],
    ['CL. 8ª — Obligaciones del Asociado', 'Manifestar por escrito la voluntad de pertenecer a la Red. Pagar la afiliación. Aportar documentos (cédula, RUT, antecedentes). Aceptar y cumplir estatutos y reglamentos. Afiliarse a la plataforma de Consumo Colaborativo.'],
    ['CL. 9ª — Obligaciones del Ente Jurídico', 'Reintegrar aportes mediante el Fondo de Amortización. Suministrar información pertinente. Retribuir beneficios de consumo colaborativo (alimentos, turismo, salud, farmacia, gimnasio, mutual, funerario y más).'],
    ['CL. 12ª — Confidencialidad', 'Las partes se obligan a guardar reserva absoluta sobre la información confidencial, no reproducirla ni utilizarla fuera de lo acordado.'],
  ];

  clausulas.forEach(([titulo, texto]) => {
    checkPage(18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...VERDE2);
    doc.text(titulo, 14, y);
    y += 4;
    y = bodyText(doc, y, texto);
    y += 3;
  });

  // ── ACUERDO TOTAL ──
  checkPage(20);
  y = sectionTitle(doc, y, 'V. ACUERDO TOTAL Y CONSENTIMIENTO');
  y += 3;
  y = bodyText(doc, y, 'El presente Contrato o Convenio Asociativo de Emprendimiento Solidario y Economías Colaborativas constituye el acuerdo total entre las partes y prevalece sobre cualquier otro acuerdo oral o escrito entre las mismas respecto del mismo objeto. Los firmantes han leído, entendido y aceptan los Términos y Condiciones escritos en el presente Contrato. Igualmente manifiestan que toda la información proporcionada es correcta.');
  y += 4;

  // ── FIRMAS ──
  checkPage(55);
  y = sectionTitle(doc, y, 'VI. FIRMAS DE LAS PARTES');
  y += 5;

  // Espacio para firmas
  const firmas = [
    { titulo: 'ASOCIACIÓN ALIANZA SOLIDARIA DE CAPELLANES', nombre: '', cedula: '' },
    { titulo: 'ASOCIADO EMPRENDEDOR / PROSUMIDOR / CAPELLÁN', nombre: `${form.nombre} ${form.apellidos}`, cedula: form.cedula },
    { titulo: 'FUNDACIÓN ALIANZA SOCIAL ORG', nombre: 'MARTHA MAGDALENA MORENO LOPERA', cedula: '43.807.453' },
    { titulo: 'FEDERACIÓN FENAGRO APL', nombre: 'JAVIER GIL CARMONA', cedula: '71.392.618' },
  ];

  const firmaW = 86, firmaH = 28;
  firmas.forEach((f, i) => {
    const fx = i % 2 === 0 ? 14 : 110;
    const fy = y + Math.floor(i / 2) * (firmaH + 8);

    doc.setDrawColor(...VERDE2);
    doc.setLineWidth(0.3);
    doc.rect(fx, fy, firmaW, firmaH);

    // Línea de firma
    doc.setDrawColor(...NEGRO);
    doc.setLineWidth(0.5);
    doc.line(fx + 5, fy + 16, fx + firmaW - 5, fy + 16);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...VERDE);
    const tituloLines = doc.splitTextToSize(f.titulo, firmaW - 8);
    doc.text(tituloLines, fx + firmaW / 2, fy + 20, { align: 'center' });

    if (f.nombre) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...NEGRO);
      doc.text(f.nombre, fx + firmaW / 2, fy + 24.5, { align: 'center' });
      doc.text(`C.C. ${f.cedula}`, fx + firmaW / 2, fy + 27.5, { align: 'center' });
    }
  });

  y += 2 * (firmaH + 8) + 6;

  // Sección de integridad y trazabilidad
  checkPage(20);
  y += 4;
  doc.setFillColor(...GRIS_CLARO);
  doc.rect(14, y, 182, hashContrato ? 16 : 10, 'F');
  doc.setDrawColor(...VERDE);
  doc.setLineWidth(0.4);
  doc.rect(14, y, 182, hashContrato ? 16 : 10);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...VERDE);
  doc.text(`No. Solicitud: #${afiliadoId ? String(afiliadoId).padStart(6,'0') : 'PENDIENTE'}`, 16, y + 4);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRIS);
  doc.text(`Fecha: ${fechaHoy}`, 80, y + 4);
  doc.text('Generado electrónicamente — Sistema FENAGRO APL', 196, y + 4, { align: 'right' });
  if (hashContrato) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...VERDE2);
    doc.text('Hash SHA-256 de integridad del contrato:', 16, y + 9);
    doc.setFont('courier', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...NEGRO);
    doc.text(hashContrato, 16, y + 13);
  }
  y += (hashContrato ? 16 : 10) + 4;

  // Footer última página
  addPageFooter(doc);

  return doc;
}

export function descargarContratoPDF(form, afiliadoId, opcionesGeo = {}, hashContrato = null) {
  const doc = generarContratoPDF(form, afiliadoId, opcionesGeo, hashContrato);
  const nombre = `${form.apellidos}_${form.nombre}`.replace(/\s+/g, '_').toUpperCase();
  doc.save(`Contrato_Afiliacion_${nombre}_${afiliadoId || 'BORRADOR'}.pdf`);
}

export function urlContratoPDF(form, afiliadoId, opcionesGeo = {}, hashContrato = null) {
  const doc = generarContratoPDF(form, afiliadoId, opcionesGeo, hashContrato);
  return doc.output('bloburl');
}
